import { useRef, useState } from "react";
import api, { resolveUrl } from "@/lib/api";
import HlsPlayer from "@/components/HlsPlayer";
import { CheckCircle2, Circle, Loader2, UploadCloud, Info, XCircle } from "lucide-react";

const STEPS = [
  { key: "create", label: "1. Création de la vidéo (brouillon)" },
  { key: "upload_url", label: "2. Demande d'URL de téléversement (provider)" },
  { key: "uploading", label: "3. Téléversement du fichier → provider" },
  { key: "asset_created", label: "4. Webhook : asset créé (preparing)" },
  { key: "ready", label: "5. Webhook : transcodage terminé (ready)" },
  { key: "published", label: "6. Publication" },
  { key: "playback", label: "7. Lecture HLS (URL signée expirante)" },
];

export default function AdminUploadTest() {
  const [state, setState] = useState({}); // key -> 'done'|'active'|'error'
  const [progress, setProgress] = useState(0);
  const [videoId, setVideoId] = useState(null);
  const [running, setRunning] = useState(false);
  const [mock, setMock] = useState(true);
  const fileRef = useRef(null);

  const set = (k, v) => setState((s) => ({ ...s, [k]: v }));

  const run = async (file) => {
    setRunning(true);
    setState({});
    setVideoId(null);
    setProgress(0);
    try {
      set("create", "active");
      const { data: v } = await api.post("/admin/videos", {
        title: { fr: `Test Pipeline ${new Date().toLocaleTimeString("fr-FR")}`, en: "" },
        description: { fr: "Vidéo de test du pipeline Mux (mock).", en: "" },
      });
      set("create", "done");

      set("upload_url", "active");
      const { data: up } = await api.post(`/admin/videos/${v.id}/create-upload`);
      setMock(up.is_mock);
      set("upload_url", "done");

      set("uploading", "active");
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", resolveUrl(up.upload_url));
        xhr.upload.onprogress = (e) => e.lengthComputable && setProgress(Math.round((e.loaded / e.total) * 100));
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject());
        xhr.onerror = reject;
        xhr.send(file);
      });
      set("uploading", "done");

      set("asset_created", "active");
      await poll(v.id, "preparing");
      set("asset_created", "done");

      set("ready", "active");
      await poll(v.id, "ready");
      set("ready", "done");

      set("published", "active");
      await api.post(`/admin/videos/${v.id}/status`, { status: "published" });
      set("published", "done");

      set("playback", "active");
      await api.get(`/videos/${v.id}/playback`);
      set("playback", "done");
      setVideoId(v.id);
    } catch (e) {
      setState((s) => {
        const active = Object.keys(s).find((k) => s[k] === "active");
        return active ? { ...s, [active]: "error" } : s;
      });
    } finally {
      setRunning(false);
    }
  };

  const poll = (id, target) =>
    new Promise((resolve, reject) => {
      let tries = 0;
      const iv = setInterval(async () => {
        tries++;
        try {
          const { data } = await api.get(`/admin/videos/${id}/transcode-status`);
          if (data.transcode_status === "errored") { clearInterval(iv); reject(); }
          if (data.transcode_status === target || (target === "preparing" && ["preparing", "ready"].includes(data.transcode_status)) || data.transcode_status === "ready") {
            clearInterval(iv); resolve();
          }
          if (tries > 30) { clearInterval(iv); reject(); }
        } catch (_) { clearInterval(iv); reject(); }
      }, 1500);
    });

  const onFile = (e) => { const f = e.target.files?.[0]; if (f) run(f); };

  const icon = (k) => {
    if (state[k] === "done") return <CheckCircle2 className="h-5 w-5 text-emerald-400" />;
    if (state[k] === "active") return <Loader2 className="h-5 w-5 animate-spin text-primary" />;
    if (state[k] === "error") return <XCircle className="h-5 w-5 text-destructive" />;
    return <Circle className="h-5 w-5 text-muted-foreground/40" />;
  };

  return (
    <div className="max-w-3xl">
      <h1 className="font-heading text-2xl font-bold tracking-tight">Pipeline Mux — Validation du chemin critique</h1>

      <div className="mt-4 flex items-start gap-2 rounded-lg border border-primary/20 bg-primary/5 p-3 text-sm text-primary/90">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <p>
          Le provider vidéo est actuellement <b>{mock ? "MOCK (simulé)" : "Mux réel"}</b>. En mode mock, le cycle
          upload → webhook <i>asset_created</i> → webhook <i>ready</i> → lecture HLS est reproduit à l'identique.
          Fournissez des clés Mux et passez <code>MOCK_MUX=false</code> pour valider le flux réel sans changer le code.
        </p>
      </div>

      <label className="mt-6 inline-flex cursor-pointer items-center gap-2 rounded-full bg-primary px-5 py-2.5 font-semibold text-primary-foreground transition-colors hover:bg-[hsl(var(--primary))]/90 aria-disabled:opacity-50">
        <UploadCloud className="h-5 w-5" /> {running ? "Test en cours…" : "Lancer un test (choisir un fichier)"}
        <input ref={fileRef} type="file" accept="video/*" className="hidden" onChange={onFile} disabled={running} data-testid="pipeline-file" />
      </label>

      <ol className="mt-6 space-y-2">
        {STEPS.map((s) => (
          <li key={s.key} className="flex items-center gap-3 rounded-lg border border-white/5 bg-[hsl(var(--surface-elevated))] px-4 py-3" data-testid={`step-${s.key}`}>
            {icon(s.key)}
            <span className={state[s.key] === "done" ? "text-foreground" : "text-muted-foreground"}>{s.label}</span>
            {s.key === "uploading" && state.uploading === "active" && (
              <div className="ml-auto h-2 w-32 overflow-hidden rounded-full bg-white/10">
                <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
              </div>
            )}
          </li>
        ))}
      </ol>

      {videoId && (
        <div className="mt-6">
          <h2 className="mb-3 font-heading text-lg font-semibold">Lecture de vérification</h2>
          <HlsPlayer videoId={videoId} />
        </div>
      )}
    </div>
  );
}
